/**
 * Information modal for Experimental App Onboarding
 * Helps users determine if experimental onboarding is right for their use case
 */
import {
  DevHubDialog,
  DevHubDialogTitle,
  DevHubDialogContent,
  DevHubDialogActions,
  DevHubButton,
  DevHubTypography,
  DevHubBox,
  DevHubGrid,
  DevHubIconButton,
  DevHubDynamicIcon,
  DevHubLink,
} from '@devxdiscover/devhub-ui';
import { ConfigHelper } from '@devxdiscover/devhub-common';

interface ExperimentalOnboardingInfoModalProps {
  open: boolean;
  onClose: () => void;
  onStartOnboarding: () => void;
}

/**
 * Modal that explains experimental onboarding and helps users self-qualify
 * @param props - The component props
 */
export const ExperimentalOnboardingInfoModal = ({
  open,
  onClose,
  onStartOnboarding,
}: ExperimentalOnboardingInfoModalProps) => {
  const slackUrl = ConfigHelper.instance.slackFeedbackUrl;

  return (
    <DevHubDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DevHubDialogTitle>
        Experimental Onboarding - Is This Right For You?
        <DevHubIconButton
          onClick={onClose}
          style={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <DevHubDynamicIcon iconName="Clear" />
        </DevHubIconButton>
      </DevHubDialogTitle>
      <DevHubDialogContent>
        <DevHubBox mb={3}>
          <DevHubTypography variant="body1" style={{ marginBottom: '16px' }}>
            This streamlined flow quickly provisions essential resources for
            temporary experiments, Lab Week projects, and short-term trials.
          </DevHubTypography>
        </DevHubBox>

        {/* What You Get Section */}
        <DevHubBox mb={3}>
          <DevHubTypography
            variant="h6"
            style={{ marginBottom: '12px', fontWeight: 600 }}
          >
            ✅ This flow is perfect if you need:
          </DevHubTypography>
          <DevHubGrid container spacing={2}>
            <DevHubGrid item xs={12} md={4}>
              <DevHubBox
                display="flex"
                flexDirection="column"
                alignItems="center"
                textAlign="center"
                padding="small"
                borderRadius="8px"
                bgcolor="#f1f8f4"
                border="1px solid #4caf50"
                height="100%"
              >
                <img
                  src="/icons/github.svg"
                  alt="GitHub"
                  style={{ width: '32px', height: '32px', marginBottom: '8px' }}
                />
                <DevHubTypography variant="body1" style={{ fontWeight: 500 }}>
                  GitHub repository
                </DevHubTypography>
                <DevHubTypography variant="body2" style={{ color: '#666' }}>
                  For source code management
                </DevHubTypography>
              </DevHubBox>
            </DevHubGrid>
            <DevHubGrid item xs={12} md={4}>
              <DevHubBox
                display="flex"
                flexDirection="column"
                alignItems="center"
                textAlign="center"
                padding="small"
                borderRadius="8px"
                bgcolor="#f1f8f4"
                border="1px solid #4caf50"
                height="100%"
              >
                <img
                  src="/icons/cnap.svg"
                  alt="CNAP"
                  style={{ width: '32px', height: '32px', marginBottom: '8px' }}
                />
                <DevHubTypography variant="body1" style={{ fontWeight: 500 }}>
                  CNAP experimentation environment
                </DevHubTypography>
                <DevHubTypography variant="body2" style={{ color: '#666' }}>
                  For deployment and testing
                </DevHubTypography>
              </DevHubBox>
            </DevHubGrid>
            <DevHubGrid item xs={12} md={4}>
              <DevHubBox
                display="flex"
                flexDirection="column"
                alignItems="center"
                textAlign="center"
                padding="small"
                borderRadius="8px"
                bgcolor="#f1f8f4"
                border="1px solid #4caf50"
                height="100%"
              >
                <img
                  src="/icons/azure.svg"
                  alt="Azure AD"
                  style={{ width: '32px', height: '32px', marginBottom: '8px' }}
                />
                <DevHubTypography variant="body1" style={{ fontWeight: 500 }}>
                  AD security groups
                </DevHubTypography>
                <DevHubTypography variant="body2" style={{ color: '#666' }}>
                  For access control and permissions
                </DevHubTypography>
              </DevHubBox>
            </DevHubGrid>
          </DevHubGrid>
        </DevHubBox>

        {/* Limitations Section */}
        <DevHubBox mb={3}>
          <DevHubTypography
            variant="h6"
            style={{ marginBottom: '12px', fontWeight: 600 }}
          >
            ⚠️ This may NOT be suitable if you also need:
          </DevHubTypography>
          <DevHubBox
            padding="medium"
            borderRadius="8px"
            bgcolor="#fff3cd"
            border="1px solid #ffc107"
          >
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>
                <DevHubTypography variant="body2">
                  Databases, message queues, or other backing services
                </DevHubTypography>
              </li>
              <li>
                <DevHubTypography variant="body2">
                  Integration with downstream provisioning systems
                </DevHubTypography>
              </li>
              <li>
                <DevHubTypography variant="body2">
                  Additional resource types beyond the basics above
                </DevHubTypography>
              </li>
              <li>
                <DevHubTypography variant="body2">
                  A permanent, production-ready application
                </DevHubTypography>
              </li>
            </ul>
          </DevHubBox>
        </DevHubBox>

        {/* Resource Reclamation Warning */}
        <DevHubBox
          mb={3}
          padding="medium"
          borderRadius="8px"
          bgcolor="#ffebee"
          border="1px solid #f44336"
        >
          <DevHubTypography variant="body2">
            🕒 <strong>Important:</strong> Experimental applications are
            designed for short-term use. Provisioned resources may be
            automatically reclaimed after a period of inactivity. This flow is
            intended for prototypes, trials, and experiments—not permanent
            applications.
          </DevHubTypography>
        </DevHubBox>

        {/* Help Section */}
        <DevHubBox
          padding="medium"
          borderRadius="8px"
          bgcolor="#e3f2fd"
          border="1px solid #2196f3"
        >
          <DevHubTypography variant="body2">
            📝 <strong>Unsure?</strong> Tell us what you need and we'll help you
            choose the right path.{' '}
            <DevHubLink
              href={slackUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Share your feedback
            </DevHubLink>
            .
          </DevHubTypography>
        </DevHubBox>
      </DevHubDialogContent>
      <DevHubDialogActions
        style={{ justifyContent: 'center', paddingBottom: '24px' }}
      >
        <DevHubButton
          variant="contained"
          onClick={() => {
            onClose();
            onStartOnboarding();
          }}
          buttonColor="pinkPurple"
          data-gtm-event="experimental-onboarding-info-proceed"
        >
          I Understand - Start Onboarding
        </DevHubButton>
      </DevHubDialogActions>
    </DevHubDialog>
  );
};
